import type { CexMark, CexSymbol } from "../api/types";
import { groupThousands } from "./money";

/**
 * Арифметика панели плотности биржи расчёта.
 *
 * Отдельным модулем, а не внутри компонента, ровно потому, что здесь живут
 * ПРАВИЛА ПУСТОТЫ, а не вёрстка: «не спрашивали», «денег нет» и «судить нечем»
 * — три разных ответа, которые в разметке слились бы в одинаковый прочерк.
 * Проверяются они тестом, а не глазами на экране.
 */

/** Разбор денежной строки. null означает «величины нет». */
function parse(value: string | null | undefined): number | null {
  if (value === undefined || value === null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Компактные доллары: $1.2M, $340K, $95. Прочерк, если величины нет. */
export function shortDollars(value: string | null | undefined): string {
  const n = parse(value);
  if (n === null) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `$${(n / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}K`;
  return `$${n.toFixed(0)}`;
}

/**
 * Цена актива: разряды и один знак после запятой.
 *
 * Отдельно от `shortDollars`, и это не дублирование. Плотность сжимать НАДО —
 * её читают порядками ($2.4M против $240K), — а цену сжимать НЕЛЬЗЯ: страйк
 * окна отличается от индекса на единицы долларов, и «79K» на месте 78 565.4
 * стирает ровно тот знак, ради которого на цену и смотрят.
 */
export function priceText(value: string | null | undefined): string {
  const n = parse(value);
  if (n === null) return "—";
  return groupThousands(n.toFixed(1));
}

/**
 * Строка отметки для зеркальных полос.
 *
 * `bidShare` и `askShare` — доли от САМОЙ ТОЛСТОЙ отметки таблицы, а не от
 * суммы сторон: кривая накопительная, и нормировка внутри строки сделала бы все
 * пять строк одинаковой длины, спрятав ровно то, ради чего кривая и хранится, —
 * во сколько раз десять базисных пунктов толще одного.
 *
 * `bidKnown`/`askKnown` едут отдельно от долей: у неспрошенной отметки доля
 * тоже ноль, и полоса нулевой длины у «не знаем» неотличима от «денег нет».
 */
export interface MarkRow {
  bp: number;
  bidText: string;
  askText: string;
  bidShare: number;
  askShare: number;
  bidKnown: boolean;
  askKnown: boolean;
}

export function markRows(marks: CexMark[]): MarkRow[] {
  let peak = 0;
  for (const m of marks) {
    if (m.have_bid) peak = Math.max(peak, parse(m.bid_usd) ?? 0);
    if (m.have_ask) peak = Math.max(peak, parse(m.ask_usd) ?? 0);
  }
  return marks.map((m) => {
    const bid = m.have_bid ? (parse(m.bid_usd) ?? 0) : null;
    const ask = m.have_ask ? (parse(m.ask_usd) ?? 0) : null;
    return {
      bp: m.bp,
      // «?» у неспрошенной отметки, а не «—»: прочерк читается как «пусто», а
      // здесь ответ «не спрашивали» — мелкий опрос до 10 бп не дотягивается.
      bidText: bid === null ? "?" : shortDollars(String(bid)),
      askText: ask === null ? "?" : shortDollars(String(ask)),
      bidShare: peak > 0 && bid !== null ? bid / peak : 0,
      askShare: peak > 0 && ask !== null ? ask / peak : 0,
      bidKnown: m.have_bid,
      askKnown: m.have_ask,
    };
  });
}

/** Тон показания. */
export type Tone = "up" | "down" | "flat" | "unknown";

/**
 * Вердикт по стене — ровно то, что ИЗМЕРЕНО, и ни словом больше.
 *
 * Замер [2026-08-26, сутки, `n ≈ 2600` на клетку]: односторонний всплеск
 * плотности предсказывает снос середины БИРЖИ на 0.5–1.2 бп в следующие
 * 30 секунд, прочь от стены. Про Polymarket он не говорит ничего: 0.5–1.2 бп на
 * порядок меньше 2.8–15.7 бп, которыми переворачивают окно.
 *
 * Поэтому подпись говорит про цену Binance, а не про исход окна. Соблазн
 * написать «кит готовит переворот» здесь прямой, и он был бы враньём.
 */
export function wallVerdict(s: CexSymbol): { text: string; tone: Tone } {
  if (!s.wall_known) {
    return { text: "фона мало — судить нечем", tone: "unknown" };
  }
  if (!s.wall) {
    return { text: "стены нет", tone: "flat" };
  }
  const times = `×${s.wall.ratio.toFixed(1)}`;
  return s.wall.side === "bid"
    ? { text: `стена бидов ${times} · цена вверх`, tone: "up" }
    : { text: `стена асков ${times} · цена вниз`, tone: "down" };
}

/**
 * Насколько ряду можно верить.
 *
 * Покрытие и свежесть — РАЗНЫЕ отказы, и объединять их в один флаг нельзя:
 * триста секунд из трёхсот, снятые десять минут назад, и сорок секунд, снятые
 * только что, портят ответ по-разному.
 */
export function coverageVerdict(s: CexSymbol): { text: string; tone: Tone } {
  if (s.secs === 0) {
    return { text: "ряда нет", tone: "unknown" };
  }
  const share = s.span_s > 0 ? s.secs / s.span_s : 0;
  if (s.stale) {
    return { text: `ряд встал ${s.age_s} с назад`, tone: "unknown" };
  }
  if (share < 0.9) {
    return { text: `${s.secs} из ${s.span_s} с — пробел`, tone: "unknown" };
  }
  return { text: `${s.secs} из ${s.span_s} с`, tone: "flat" };
}

/** Перекос словами. Прочерк отличается от нуля: ноль означает равенство сторон. */
export function imbalanceText(s: CexSymbol): string {
  if (s.imbalance === undefined || s.imbalance === null) return "—";
  const pct = Math.round(s.imbalance * 100);
  if (pct === 0) return "поровну";
  return pct > 0 ? `биды +${pct}%` : `аски +${-pct}%`;
}

/**
 * Точки спарклайна в координатах SVG.
 *
 * Пробелы ряда рвут линию, а не соединяют её: соединённый пробел выката — это
 * нарисованный плавный переход там, где данных не было вовсе.
 */
export function sparkPaths(series: (number | null)[], width: number, height: number): string[] {
  if (series.length < 2) return [];
  const out: string[] = [];
  let cur: string[] = [];
  series.forEach((v, i) => {
    if (v === null || v === undefined) {
      if (cur.length > 1) out.push(cur.join(" "));
      cur = [];
      return;
    }
    const x = (i / (series.length - 1)) * width;
    // Перекос живёт в [−1, +1]; ноль — середина высоты.
    const y = height / 2 - (Math.max(-1, Math.min(1, v)) * height) / 2;
    cur.push(`${cur.length === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`);
  });
  if (cur.length > 1) out.push(cur.join(" "));
  return out;
}
